import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, Check, Sparkles, Heart, ArrowRight, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { METROS } from '../mock';
import api from '../api';

const SLUG_TO_METRO = { portland: 'Portland, OR', eugene: 'Eugene/Springfield, OR', sanfrancisco: 'San Francisco, CA', other: 'Other' };
const CITY_LABEL = { portland: 'Portland', eugene: 'Eugene & Springfield', sanfrancisco: 'San Francisco' };

export default function Beta() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const src = sp.get('src') || '';
  const slug = (sp.get('city') || '').toLowerCase();
  const presetCity = SLUG_TO_METRO[slug] || METROS.find((m) => m.toLowerCase().includes(slug) && slug) || '';
  const cityLabel = CITY_LABEL[slug] || '';

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [city, setCity] = useState(presetCity || METROS[0]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) { toast.error('Please enter your email'); return; }
    setBusy(true);
    try {
      const r = await api.post('/beta/waitlist', { email, name, city, source: src });
      setDone(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Something went wrong');
    } finally { setBusy(false); }
  };

  const copyCode = () => { navigator.clipboard.writeText(done.invite_code); toast.success('Code copied'); };

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto grid min-h-screen max-w-6xl lg:grid-cols-2">
        {/* Left / hero */}
        <div className="relative flex flex-col justify-center overflow-hidden bg-[hsl(var(--coral))] px-8 py-14 text-white lg:px-14">
          <div className="relative z-10 max-w-md">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/20 px-3 py-1 text-xs font-bold backdrop-blur">
              <Sparkles className="h-3.5 w-3.5" /> {cityLabel ? `${cityLabel} · Founding Beta` : 'Founding Beta'}
            </div>
            <h1 className="mt-5 text-4xl font-extrabold leading-tight sm:text-5xl">
              {cityLabel ? `${cityLabel} cat parents, you're invited.` : 'Be one of the first cat parents on Purrflect.'}
            </h1>
            <p className="mt-4 text-base text-white/90">Join the {cityLabel ? `${cityLabel} ` : 'local '}beta and get <b>90 days of PRO free</b> — unlimited AI vet chats, symptom photo checks, and vaccine reminders for your cat.</p>
            <ul className="mt-6 space-y-2.5 text-sm">
              {['Unlimited AI vet guidance, day or night', 'Snap a photo for a symptom check', 'Never miss a vaccine or vet visit'].map((p) => (
                <li key={p} className="flex items-center gap-2.5"><span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/25"><Check className="h-3.5 w-3.5" /></span>{p}</li>
              ))}
            </ul>
          </div>
          <Heart className="pointer-events-none absolute -bottom-10 -right-6 h-56 w-56 text-white/10" />
        </div>

        {/* Right / form */}
        <div className="flex items-center justify-center px-6 py-14">
          <div className="w-full max-w-md animate-fade-up">
            {!done ? (
              <>
                <h2 className="text-2xl font-extrabold">Reserve your spot</h2>
                <p className="mt-1.5 text-muted-foreground">We're onboarding 50 cat parents per city. Tell us where you are.</p>
                <form onSubmit={submit} className="mt-7 space-y-4" data-testid="beta-form">
                  <div>
                    <Label className="text-sm font-semibold">Your name <span className="font-normal text-muted-foreground">(optional)</span></Label>
                    <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane" className="mt-1.5 h-12 rounded-xl bg-white" />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold">Email</Label>
                    <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="beta-email" className="mt-1.5 h-12 rounded-xl bg-white" />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold">Your city</Label>
                    <select value={city} onChange={(e) => setCity(e.target.value)} data-testid="beta-city" className="mt-1.5 h-12 w-full rounded-xl border border-input bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-[hsl(var(--coral))]">
                      {METROS.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                  <button type="submit" disabled={busy} data-testid="beta-submit" className="coral-btn flex w-full items-center justify-center gap-2 rounded-2xl py-4 text-base font-bold text-white disabled:opacity-70">
                    {busy && <Loader2 className="h-5 w-5 animate-spin" />}Join the beta
                  </button>
                </form>
              </>
            ) : (
              <div data-testid="beta-success" className="rounded-3xl bg-white p-7 text-center card-soft">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-accent"><Check className="h-7 w-7 text-[hsl(var(--coral))]" /></div>
                <h2 className="mt-4 text-2xl font-extrabold">You're on the list! 🐱</h2>
                <p className="mt-2 text-sm text-muted-foreground">We emailed your details. Use this code when you sign up to unlock <b>{done.pro_days} days of PRO free</b>:</p>
                <button onClick={copyCode} className="mx-auto mt-4 flex items-center gap-2 rounded-xl border-2 border-dashed border-[hsl(var(--coral))] bg-accent/40 px-5 py-3 text-xl font-extrabold tracking-widest text-[hsl(var(--coral))]" data-testid="beta-code">
                  {done.invite_code} <Copy className="h-4 w-4" />
                </button>
                <button onClick={() => nav(`/auth?code=${done.invite_code}`)} className="coral-btn mt-6 flex w-full items-center justify-center gap-2 rounded-2xl py-3.5 text-base font-bold text-white">
                  Create my account <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            )}
            <p className="mt-6 text-center text-sm text-muted-foreground">Are you a vet clinic? <a href="/vets/join" className="font-bold text-[hsl(var(--coral))]">Become a founding partner</a></p>
          </div>
        </div>
      </div>
    </div>
  );
}
